export type AuthAccountType = "restaurant" | "consumer";
export type AuthRole = "admin" | "user" | "consumer";

type SessionLike =
  | {
      user?: {
        role?: unknown;
      } | null;
    }
  | null
  | undefined;

export function getAuthRoles(role: unknown): string[] {
  if (Array.isArray(role)) {
    const roles = role.filter((value): value is string => typeof value === "string");
    return roles.length > 0 ? roles : ["user"];
  }

  if (typeof role === "string" && role.trim()) {
    return role
      .split(",")
      .map((value) => value.trim())
      .filter(Boolean);
  }

  return ["user"];
}

export function hasAuthRole(role: unknown, expected: AuthRole): boolean {
  return getAuthRoles(role).includes(expected);
}

export function isConsumerRole(role: unknown): boolean {
  return hasAuthRole(role, "consumer");
}

export function isRestaurantRole(role: unknown): boolean {
  const roles = getAuthRoles(role);
  return roles.includes("admin") || roles.includes("user");
}

export function matchesAuthAccountType(accountType: AuthAccountType, role: unknown): boolean {
  return accountType === "consumer" ? isConsumerRole(role) : isRestaurantRole(role);
}

export function isConsumerSession(session: SessionLike): boolean {
  return isConsumerRole(session?.user?.role);
}

export function isRestaurantSession(session: SessionLike): boolean {
  return isRestaurantRole(session?.user?.role);
}

export function getDefaultAuthRedirect(accountType: AuthAccountType): string {
  return accountType === "consumer" ? "/account" : "/";
}

export function getSafeRedirectTarget(next: string | null, fallback: string): string {
  if (!next || !next.startsWith("/") || next.startsWith("//")) {
    return fallback;
  }

  return next.startsWith("/auth") ? fallback : next;
}

export function getAuthPath(
  accountType: AuthAccountType,
  action: "sign-in" | "sign-up",
  next?: string | null,
): string {
  const path = `/auth/${accountType}/${action}`;
  const safeNext = getSafeRedirectTarget(next ?? null, "");

  return safeNext ? `${path}?next=${encodeURIComponent(safeNext)}` : path;
}
